import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table, { TableRow } from 'components/Table';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetchMyCellarPaged, fetchMyCellarDetails } from 'views/MyCellar/services/fetchMyCellar';
import { logError } from 'components/LogError';
import { DDFilterItem, FilterTypes } from '../../components/Filters';
import { SortAndFilterLayoutContextType, TabState } from '../../components/Layout/SortAndFilterLayout';
import StockTab from '../../components/StockTab';
import { AppContext } from '../../context/ContextProvider';
import { getBlankProduct } from '../../helpers';
import { AppEventTypes } from '../../types/AppType';
import { NavigationPath } from '../../types/DomainTypes';
import { Product, BaseProduct, DATA_REFS } from '../../types/productType';
import { sortItems, toInternalId, uniqueItems } from '../../utils';
import useFadeInOnScroll from '../hooks/useFadeInOnScroll';
import { buildTableRow, initialiseMyCellarExportColumns } from '../Portfolio/helpers';
import { SortByOption } from '../Portfolio/types';
import ProtectedPageLayout from '../shared/ProtectedPageLayout';
import CellarSummary from './components/CellarSummary';
import RegionalBreakdownBar from './components/RegionalBreakdownBar';
import CellarWineCard from './components/CellarWineCard';
import MarketInsightsPanel from './components/MarketInsightsPanel';
import { useCellarStats } from './hooks/useCellarStats';
import { useCellarInsights } from './hooks/useCellarInsights';
import { usePortalHoldings } from './hooks/usePortalHoldings';
import { usePortfolioReturns } from '../../hooks/usePortfolioReturns';

const MyCellar = () => {
  const { t } = useTranslation();
  const {
    state: {
      app: { refresh },
      settings: { selectedPortfolioId },
    },
    dispatch,
    gbpToTargetCurrency,
  } = useContext(AppContext);
  const [selectedAssetUnitId, setSelectedAssetUnitId] = useState<string>('');
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<string | null>(null);

  const PAGE_SIZE = 50; // Larger page size for faster loading

  const {
    data: myCellarStockVT2,
    isLoading: loadingMyCellarVT2,
    isFetching: isFetchingCellar,
    error: myCellarError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryFn: ({ pageParam = 0 }) => fetchMyCellarPaged(pageParam, PAGE_SIZE, selectedPortfolioId || undefined),
    queryKey: ['fetchMyCellar', { selectedPortfolioId }],
    staleTime: 2 * 60 * 1000, // Data stays fresh for 2 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    getNextPageParam: (lastPage, allPages) => {
      // If we got fewer items than page size, there are no more pages
      if (!lastPage.data || lastPage.data.length < PAGE_SIZE) return undefined;
      // Stop if the page brought nothing new. A full page of rows we already
      // hold means the server is replaying the same set rather than paging
      // through it, and asking again would only duplicate rows.
      const previous = new Set(
        allPages.slice(0, -1).flatMap((page) => (page.data ?? []).map((item) => item.assetUnitId)),
      );
      if (previous.size > 0 && lastPage.data.every((item) => previous.has(item.assetUnitId))) return undefined;
      // Return the next page number
      return allPages.length;
    },
  });

  const {
    columns,
    sortByOptions,
    defaultSortBy: currentDefaultSortBy,
  } = useMemo(() => initialiseMyCellarExportColumns(t), [t]);

  // Flatten all pages into a single array
  const myCellarStock = useMemo(() => {
    if (loadingMyCellarVT2 || !myCellarStockVT2?.pages) return [];
    const allItems = myCellarStockVT2.pages.flatMap((page) => page.data ?? []);
    // One row per asset unit: pages must never contribute the same unit twice,
    // or it is counted twice in the cellar totals and stats.
    const uniqueItemsByUnit = uniqueItems(allItems, 'assetUnitId');
    return sortItems(
      uniqueItemsByUnit,
      currentDefaultSortBy.direction !== 'asc',
      currentDefaultSortBy.id as keyof Product,
    );
  }, [myCellarStockVT2, loadingMyCellarVT2, currentDefaultSortBy]);

  const fetchMyCellarDetailsApi = (assetUnitId: string) => {
    if (!selectedAssetUnitId) return Promise.resolve(null);
    return fetchMyCellarDetails({ assetUnitId });
  };

  const {
    data: myCellarDetailsVT2,
    isFetching,
    error: fetchMyCellarDetailsApiError,
    refetch: refetchMyCellar,
  } = useQuery({
    queryFn: () => fetchMyCellarDetailsApi(selectedAssetUnitId),
    queryKey: ['fetchMyCellarDetails', { assetUnitId: selectedAssetUnitId }],
    enabled: !!selectedAssetUnitId,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });

  const currentTabState = useRef<TabState | null>(null);
  const pageTitle = 'cellar';
  const title = t(`common:${pageTitle}`);
  const [filteredData, setFilteredData] = useState<Product[]>([]);

  // Calculate stats (value/P&L come from current holdings — same source as Portfolio "Portfolio Valuation").
  // Pass selectedPortfolioId to filter stats by portfolio (null/-1 = all portfolios)
  const cellarStats = useCellarStats(t, selectedPortfolioId);

  // Calculate insights from the full dataset
  const cellarInsights = useCellarInsights(
    myCellarStock as Product[],
    cellarStats.regionAllocations,
    cellarStats.totalValue,
  );

  // Fetch market insights from PortalCurrentHoldings API
  const marketInsights = usePortalHoldings(selectedPortfolioId);
  // Actual portfolio performance, from the same valuation series as the Portfolio page
  const portfolioReturns = usePortfolioReturns(selectedPortfolioId);

  const onStateChange = (state: TabState | null) => {
    const update = state?.filters?.data;
    if (update !== filteredData) {
      setFilteredData(update || []);
    }
    currentTabState.current = state;
  };

  const stocks = useMemo(() => {
    if (!myCellarStock) return [];
    const adjustQtySource = (myCellarStock as BaseProduct[])
      .map((x) => ({ ...getBlankProduct(), ...x }))
      .map((x) => {
        return {
          ...x,
          qty: x.rotationNumber && x.rotationNumber.length > 0 ? 1 : x.qty,
          [DATA_REFS.SANITIZED_WINE_NAME]: x[DATA_REFS.NAME].normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
        };
      });
    return sortItems(
      [...adjustQtySource],
      currentDefaultSortBy.direction === 'asc',
      currentDefaultSortBy.id as keyof unknown,
    );
  }, [myCellarStock, currentDefaultSortBy]);

  // Apply regional filter to filtered data
  const displayData = useMemo(() => {
    if (!selectedRegionFilter) return filteredData;
    return filteredData.filter(
      (item) => toInternalId(item.cultWinesAllocationRegion?.toLowerCase() || 'other') === selectedRegionFilter,
    );
  }, [filteredData, selectedRegionFilter]);

  // Server-side pagination: use IntersectionObserver to fetch more when near bottom
  const loadMoreRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadMoreRef.current) loadMoreRef.current.disconnect();
      if (!node || isFetchingNextPage || !hasNextPage) return;

      loadMoreRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1, rootMargin: '100px' }, // Prefetch 100px before reaching bottom
      );

      loadMoreRef.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );
  const processIntersectionObserverEntry = useCallback((entry: IntersectionObserverEntry) => {
    entry.target.classList.toggle('flex', entry.isIntersecting);
  }, []);

  const { isItemVisible, fadeOnScrollClassName } = useFadeInOnScroll({
    isLoading: loadingMyCellarVT2,
    processIntersectionObserverEntry,
  });

  const statuses = useMemo(() => {
    if (!myCellarStock) return [];
    return uniqueItems((myCellarStock as BaseProduct[]).map((x) => x.status));
  }, [myCellarStock]);

  const onItemSelect = (item: Product, context: SortAndFilterLayoutContextType | undefined) => {
    if (item.assetUnitId) {
      setSelectedAssetUnitId(item.assetUnitId);

      // Open slideout immediately with basic item data
      // Details will be enriched when the API call completes
      const enrichItemForSlideout = (baseItem: Product, detailsData?: Product | null) => {
        let enrichedItem: Product = detailsData || baseItem;

        if (enrichedItem?.highestBid?.price) {
          enrichedItem = {
            ...enrichedItem,
            highestBid: {
              ...enrichedItem.highestBid,
              priceInTargetCurrency: gbpToTargetCurrency.convert(enrichedItem.highestBid?.price ?? 0),
              priceGbp: enrichedItem.highestBid.price,
            },
          };
        }

        if (enrichedItem?.lowestOffer?.price) {
          enrichedItem = {
            ...enrichedItem,
            lowestOffer: {
              ...enrichedItem.lowestOffer,
              priceInTargetCurrency: gbpToTargetCurrency.convert(enrichedItem.lowestOffer?.price ?? 0),
              priceGbp: enrichedItem.lowestOffer.price,
            },
          };
        }

        // Enrich with review data from marketInsights
        const holdingMarketData = marketInsights.getHoldingInsight(enrichedItem.lwin18);
        if (holdingMarketData) {
          if (holdingMarketData.waReviewer || holdingMarketData.waScore || holdingMarketData.wa) {
            enrichedItem = {
              ...enrichedItem,
              wa: holdingMarketData.wa ?? holdingMarketData.waScore,
              wineAdvocateReview: {
                reviewer: holdingMarketData.waReviewer,
                scoreDate: holdingMarketData.waScoreDate,
                score: holdingMarketData.waScore,
                drinkYearMin: holdingMarketData.waDrinkYearMin,
                drinkYearMax: holdingMarketData.waDrinkYearMax,
                tastingNote: holdingMarketData.waTastingNote,
                articleUrl: holdingMarketData.waArticleUrl,
              },
            };
          }

          if (holdingMarketData.vReviewer || holdingMarketData.vScoreMedian || holdingMarketData.vinous) {
            enrichedItem = {
              ...enrichedItem,
              vinous: holdingMarketData.vinous ?? holdingMarketData.vScoreMedian?.toString() ?? null,
              vinousReview: {
                reviewer: holdingMarketData.vReviewer,
                reviewDate: holdingMarketData.vReviewDate,
                score: holdingMarketData.vScoreMedian,
                drinkFrom: holdingMarketData.vDrinkFrom,
                drinkTo: holdingMarketData.vDrinkTo,
                tastingNote: holdingMarketData.vTastingNote,
                externalLink: holdingMarketData.vExternalLink,
              },
            };
          }

          // Add quality metrics for Market Metrics section
          enrichedItem = {
            ...enrichedItem,
            grade: holdingMarketData.grade ?? enrichedItem.grade,
            fairValueScore: holdingMarketData.fairValueScore ?? enrichedItem.fairValueScore,
            liquidityScore: holdingMarketData.liquidityScore ?? enrichedItem.liquidityScore,
            performanceScore: holdingMarketData.performanceScore ?? enrichedItem.performanceScore,
            vintageScore: holdingMarketData.vintageScore ?? enrichedItem.vintageScore,
            marketPrice: holdingMarketData.marketPrice ?? enrichedItem.marketPrice,
            // Use normalizedCriticScore for combined critic score
            combinedScore: holdingMarketData.normalizedCriticScore ?? enrichedItem.combinedScore,
          };
        }

        return enrichedItem;
      };

      // Open slideout immediately with the basic item data
      context?.openSlideoutWithProcessor(() => {
        return Promise.resolve(enrichItemForSlideout(item, null));
      });
    }
  };

  const buildTableRows = (dataSet: Product[]) => {
    return (dataSet || []).map((report) => {
      return {
        ...buildTableRow({ ...report }, columns || [], `${report.holdingId}`, 'divide-x-0'),
      };
    });
  };

  // Use displayData directly - server-side pagination handles loading
  const results = displayData as Product[];

  const tableRows = useMemo(() => {
    return buildTableRows(displayData as Product[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayData]);

  useLayoutEffect(() => {
    if (refresh && (refresh as string[]).length > 0 && (refresh as string[]).includes(NavigationPath.MY_CELLAR)) {
      refetchMyCellar();
      dispatch({
        type: AppEventTypes.UPDATE_STATE,
        payload: { refresh: (refresh as string[]).filter((x) => x !== NavigationPath.MY_CELLAR) },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Details are fetched in background for caching - slideout opens immediately with basic item data
  // Clear selected state when details finish loading
  useEffect(() => {
    if (!isFetching && selectedAssetUnitId && myCellarDetailsVT2) {
      // Data is now cached for future use
      setSelectedAssetUnitId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, myCellarDetailsVT2]);

  useEffect(() => {
    if (fetchMyCellarDetailsApiError) {
      logError(JSON.stringify(fetchMyCellarDetailsApiError));
    }
  }, [fetchMyCellarDetailsApiError]);

  useEffect(() => {
    if (myCellarError) {
      logError(JSON.stringify(myCellarError));
    }
  }, [myCellarError]);

  const handleRegionFilterChange = (regionId: string | null) => {
    setSelectedRegionFilter(regionId);
  };

  // Show initial loading only on first load (no cached data)
  const isInitialLoading = loadingMyCellarVT2 && !myCellarStockVT2;

  return (
    <ProtectedPageLayout view={NavigationPath.MY_CELLAR} title={title} onBack={() => null} showBackButton={false}>
      <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-900">
        {/* Hero Summary Section */}
        <div className="bg-white dark:bg-slate-800 shadow-sm">
          <CellarSummary stats={cellarStats} loading={isInitialLoading || cellarStats.isLoading} />
        </div>

        {/* Market Insights Panel */}
        <div className="px-4 sm:px-6 py-3 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Market Insights</h3>
          <MarketInsightsPanel
            insights={marketInsights}
            portfolioReturns={portfolioReturns.returns}
            isLoadingReturns={portfolioReturns.isLoading}
            formatCurrency={gbpToTargetCurrency.convertAndFormat}
          />
        </div>

        {/* Regional Breakdown Bar */}
        <div className="bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700">
          <RegionalBreakdownBar
            allocations={cellarStats.regionAllocations}
            onRegionClick={handleRegionFilterChange}
            selectedRegion={selectedRegionFilter}
            concentrationWarnings={cellarInsights.concentrationWarnings}
            onDismissConcentrationWarning={cellarInsights.dismissInsight}
            dismissedWarnings={cellarInsights.dismissedInsights}
          />
        </div>

        {/* Filter/Sort Bar and Wine Collection */}
        <StockTab
          selectedPortfolioId={selectedPortfolioId}
          disableExport={true}
          columns={columns}
          sortByOptions={sortByOptions as SortByOption[]}
          defaultSortBy={currentDefaultSortBy}
          tabState={null}
          id="myCellar"
          datasource={stocks as Product[]}
          isCardViewDefault={true}
          onStateChange={onStateChange}
          loading={isInitialLoading}
          error={myCellarError ? new Error(t('common:somethingWentWrong.subTitle')) : null}
          totalCount={myCellarStockVT2?.pages?.[0]?.totalCount}
          filterConfigure={(filters: DDFilterItem[]) => {
            return filters.filter((x) => x.type !== FilterTypes.P_AND_L);
          }}
          filterPanelContainerClassName="px-4 sm:px-6 py-3 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700"
          filterOverrides={{
            [FilterTypes.STATUES]: {
              source: statuses,
              filterFn: (data: Product[], ids: string[]) => {
                let statusesFilterResult: Product[] = [];
                statuses.forEach((status) => {
                  if (ids.includes(toInternalId(status!))) {
                    statusesFilterResult = [
                      ...statusesFilterResult,
                      ...data.filter((x) => x.status?.toLowerCase() === status?.toLowerCase()),
                    ];
                  }
                });
                return statusesFilterResult;
              },
            },
          }}
        >
          {(context) =>
            context?.cardLayoutConfig?.show ? (
              <div className="flex flex-col flex-1 bg-gray-50 dark:bg-slate-900">
                {/* Background refresh indicator */}
                {isFetchingCellar && !isInitialLoading && (
                  <div className="flex justify-center py-3 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-10">
                    <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-700 rounded-full shadow-sm">
                      <svg className="animate-spin h-3 w-3 text-vine" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Refreshing...
                    </span>
                  </div>
                )}
                <div
                  className="grid gap-4 sm:gap-5 p-4 sm:p-6"
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))' }}
                >
                  {isInitialLoading
                    ? // Skeleton cards while loading
                      Array.from({ length: 8 }).map((_, index) => (
                        <div
                          key={`skeleton-${index}`}
                          className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-4 animate-pulse"
                        >
                          <div className="h-28 bg-gray-100 dark:bg-slate-700 rounded-xl mb-3" />
                          <div className="h-5 bg-gray-100 dark:bg-slate-700 rounded-lg w-4/5 mb-2" />
                          <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded-lg w-1/2 mb-4" />
                          <div className="flex justify-between pt-3 border-t border-gray-100 dark:border-slate-700">
                            <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-1/3" />
                            <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-1/4" />
                          </div>
                        </div>
                      ))
                    : results.map((item, index) => {
                        let lastElemRefOption = { ref: isItemVisible };
                        if (results.length === index + 1) {
                          lastElemRefOption = { ref: lastItemRef };
                        }
                        const id = `${item.id}-${index}`;
                        const wineInsight = cellarInsights.getWineInsight(item);
                        const holdingMarketData = marketInsights.getHoldingInsight(item.lwin18);

                        return (
                          <CellarWineCard
                            {...lastElemRefOption}
                            key={id}
                            item={item}
                            onClick={() => onItemSelect(item, context)}
                            className={`${fadeOnScrollClassName} w-full`}
                            insights={wineInsight}
                            onDismissInsight={(type) => {
                              cellarInsights.dismissInsight(`${item.id}:${type}`);
                            }}
                            marketData={holdingMarketData}
                          />
                        );
                      })}
                </div>
                {!isInitialLoading && results.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-gray-400 dark:text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 font-medium">
                      {selectedRegionFilter ? t('common:noWinesInRegion') : t('common:noWinesFound')}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters</p>
                  </div>
                )}
                {/* Loading more indicator */}
                {isFetchingNextPage && (
                  <div className="flex justify-center py-6">
                    <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 rounded-full shadow-sm">
                      <svg className="animate-spin h-4 w-4 text-vine" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Loading more wines...
                    </span>
                  </div>
                )}
                <div className="flex-1 pb-6" />
              </div>
            ) : (
              <div className="flex justify-center flex-1 bg-gray-50 dark:bg-slate-900 p-4 sm:p-6">
                <div className="w-full bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
                  <Table
                    containerClassNames="w-full"
                    columns={columns || []}
                    rows={tableRows || []}
                    onTableEvent={(row) => {
                      const item = row as TableRow;
                      const selectedItem = item.rowData as unknown as Product;
                      onItemSelect(selectedItem, context);
                    }}
                  />
                </div>
              </div>
            )
          }
        </StockTab>
      </div>
    </ProtectedPageLayout>
  );
};

export default MyCellar;
